import type { ConditionalRoutingRule, IdentifierRecord } from '@myplayer/schema';

export interface ScheduledLinkChange {
  readonly targetHref: string;
  readonly validFrom: string;
  readonly formattedDate: string;
}

export interface AppliedScheduledLinkChange {
  readonly ruleId: string;
  readonly targetHref: string;
  readonly validFrom: string;
  readonly appliedAt: string;
  readonly formattedDate: string;
}

export interface ActiveRuleOverride {
  readonly ruleId: string;
  readonly targetHref: string;
  readonly linkType?: string;
}

export function formatScheduledDateLabel(isoString: string): string {
  const parsed = Date.parse(isoString);
  if (Number.isNaN(parsed)) return isoString;
  const dateObj = new Date(parsed);
  const datePart = dateObj.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = dateObj.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return timePart === '00:00' ? datePart : `${datePart}, ${timePart}`;
}

export function isScheduledRuleExpired(
  rule: {
    readonly id: string;
    readonly active?: boolean;
    readonly condition?: { readonly validFrom?: string; readonly validUntil?: string };
  },
  nowMs: number = Date.now(),
): boolean {
  if (!rule.id.startsWith('schedule-')) return false;
  if (rule.active === false) return true;
  const fromStr = rule.condition?.validFrom?.trim();
  const untilStr = rule.condition?.validUntil?.trim();
  const fromMs = fromStr ? Date.parse(fromStr) : Number.NaN;
  const untilMs = untilStr ? Date.parse(untilStr) : Number.NaN;
  if (!Number.isNaN(untilMs)) {
    return untilMs <= nowMs;
  }
  if (!Number.isNaN(fromMs)) {
    return fromMs <= nowMs;
  }
  return false;
}

export function resolveScheduledChange(
  rec: IdentifierRecord,
  nowMs: number = Date.now(),
): ScheduledLinkChange | null {
  const rules = rec.draft?.rules ?? rec.rules ?? [];
  for (const rule of rules) {
    const validFrom = rule.condition?.validFrom?.trim();
    const isActive = (rule as { readonly active?: boolean }).active !== false;
    if (
      isActive &&
      validFrom &&
      validFrom.length > 0 &&
      rule.targetHref.trim().length > 0 &&
      !isScheduledRuleExpired(rule, nowMs)
    ) {
      return {
        targetHref: rule.targetHref.trim(),
        validFrom,
        formattedDate: formatScheduledDateLabel(validFrom),
      };
    }
  }
  return null;
}

export function resolvePendingFiredScheduledChange(
  rec: IdentifierRecord,
  nowMs: number = Date.now(),
): ScheduledLinkChange | null {
  const recLinks = rec.draft?.links ?? rec.links ?? [];
  const defaultLink =
    recLinks.find((l) => l.removed !== true && l.defaultLink) ??
    recLinks.find((l) => l.removed !== true);
  const currentDefaultHref = (
    defaultLink?.href ??
    rec.draft?.defaultUrl ??
    rec.defaultUrl ??
    ''
  ).trim();
  const rules = [...(rec.draft?.rules ?? []), ...(rec.rules ?? [])];
  for (const rule of rules) {
    if (!rule.id.startsWith('schedule-')) continue;
    if ((rule as { readonly active?: boolean }).active === false) continue;
    const validFrom = rule.condition?.validFrom?.trim();
    const validUntil = rule.condition?.validUntil?.trim();
    const fromMs = validFrom ? Date.parse(validFrom) : Number.NaN;
    const untilMs = validUntil ? Date.parse(validUntil) : Number.NaN;
    const href = rule.targetHref.trim();
    if (
      href.length > 0 &&
      !Number.isNaN(fromMs) &&
      fromMs <= nowMs &&
      (Number.isNaN(untilMs) || untilMs > nowMs) &&
      (href !== rec.defaultUrl || href !== currentDefaultHref)
    ) {
      return {
        targetHref: href,
        validFrom: validFrom ?? '',
        formattedDate: formatScheduledDateLabel(validFrom ?? ''),
      };
    }
  }
  const rawSc = (rec as { readonly scheduledChange?: Record<string, unknown> | null })
    .scheduledChange;
  if (rawSc && typeof rawSc === 'object') {
    if (rawSc['active'] === false || rawSc['status'] === 'APPLIED') return null;
    const href = (
      typeof rawSc['targetHref'] === 'string'
        ? rawSc['targetHref']
        : typeof rawSc['targetUrl'] === 'string'
          ? rawSc['targetUrl']
          : ''
    ).trim();
    const validFrom = (
      typeof rawSc['validFrom'] === 'string'
        ? rawSc['validFrom']
        : typeof rawSc['scheduledAt'] === 'string'
          ? rawSc['scheduledAt']
          : ''
    ).trim();
    const fromMs = validFrom ? Date.parse(validFrom) : Number.NaN;
    if (
      href.length > 0 &&
      !Number.isNaN(fromMs) &&
      fromMs <= nowMs &&
      (href !== rec.defaultUrl || href !== currentDefaultHref)
    ) {
      return {
        targetHref: href,
        validFrom,
        formattedDate: formatScheduledDateLabel(validFrom),
      };
    }
  }
  return null;
}

export function resolveAppliedScheduledChange(
  rec: IdentifierRecord,
  nowMs: number = Date.now(),
): AppliedScheduledLinkChange | null {
  if (resolveScheduledChange(rec, nowMs) !== null) return null;
  if (resolvePendingFiredScheduledChange(rec, nowMs) !== null) return null;
  const rules = rec.draft?.rules ?? rec.rules ?? [];
  for (const rule of rules) {
    if (!rule.id.startsWith('schedule-')) continue;
    const validFrom = rule.condition?.validFrom?.trim();
    const validUntil = rule.condition?.validUntil?.trim();
    const fromMs = validFrom ? Date.parse(validFrom) : Number.NaN;
    const untilMs = validUntil ? Date.parse(validUntil) : Number.NaN;
    const href = rule.targetHref.trim();
    if (
      href.length > 0 &&
      !Number.isNaN(fromMs) &&
      fromMs <= nowMs &&
      (Number.isNaN(untilMs) || untilMs > nowMs)
    ) {
      return {
        ruleId: rule.id,
        targetHref: href,
        validFrom: validFrom ?? '',
        appliedAt: validFrom ?? '',
        formattedDate: formatScheduledDateLabel(validFrom ?? ''),
      };
    }
  }
  const rawSc = (rec as { readonly scheduledChange?: Record<string, unknown> | null })
    .scheduledChange;
  if (rawSc && typeof rawSc === 'object') {
    const href = (
      typeof rawSc['targetHref'] === 'string'
        ? rawSc['targetHref']
        : typeof rawSc['targetUrl'] === 'string'
          ? rawSc['targetUrl']
          : ''
    ).trim();
    const validFrom = (
      typeof rawSc['validFrom'] === 'string'
        ? rawSc['validFrom']
        : typeof rawSc['scheduledAt'] === 'string'
          ? rawSc['scheduledAt']
          : ''
    ).trim();
    const appliedAt = (
      typeof rawSc['appliedAt'] === 'string' && rawSc['appliedAt'].trim().length > 0
        ? rawSc['appliedAt']
        : validFrom
    ).trim();
    const fromMs = validFrom ? Date.parse(validFrom) : Number.NaN;
    const isApplied =
      rawSc['status'] === 'APPLIED' ||
      (rawSc['status'] !== 'SUPERSEDED' &&
        rawSc['status'] !== 'CANCELLED' &&
        (rawSc['active'] === false || (!Number.isNaN(fromMs) && fromMs <= nowMs)));
    if (href.length > 0 && appliedAt.length > 0 && isApplied) {
      return {
        ruleId: typeof rawSc['ruleId'] === 'string' ? rawSc['ruleId'] : 'schedule-1',
        targetHref: href,
        validFrom: validFrom || appliedAt,
        appliedAt,
        formattedDate: formatScheduledDateLabel(appliedAt),
      };
    }
  }
  return null;
}

export function resolveActiveRuleOverride(
  rec: IdentifierRecord,
  nowMs: number = Date.now(),
): ActiveRuleOverride | null {
  const liveRules = (rec.rules ?? []).slice().sort((a, b) => a.priority - b.priority);
  for (const rule of liveRules) {
    const r = rule as { readonly active?: boolean; readonly enabled?: boolean };
    if (r.active === false || r.enabled === false) continue;
    const href = typeof rule.targetHref === 'string' ? rule.targetHref.trim() : '';
    if (href.length === 0) continue;
    const validFrom = rule.condition?.validFrom?.trim();
    const validUntil = rule.condition?.validUntil?.trim();
    if (validFrom && validFrom.length > 0) {
      const fromMs = Date.parse(validFrom);
      if (!Number.isNaN(fromMs) && fromMs > nowMs) continue;
    }
    if (validUntil && validUntil.length > 0) {
      const untilMs = Date.parse(validUntil);
      if (!Number.isNaN(untilMs) && untilMs <= nowMs) continue;
    }
    return {
      ruleId: rule.id,
      targetHref: href,
      ...(rule.linkType !== undefined ? { linkType: rule.linkType } : {}),
    };
  }
  return null;
}

export function buildScheduledRulesList(params: {
  readonly existingRules: readonly ConditionalRoutingRule[];
  readonly scheduledDateIso: string | null;
  readonly scheduledTargetUrl: string | null;
}):
  | { readonly ok: true; readonly rules: readonly ConditionalRoutingRule[] }
  | { readonly ok: false; readonly error: string } {
  const nonScheduleRules = params.existingRules.filter((r) => !r.id.startsWith('schedule-'));
  const rawDate = params.scheduledDateIso?.trim() ?? '';
  const rawTarget = params.scheduledTargetUrl?.trim() ?? '';
  if (rawDate.length === 0 && rawTarget.length === 0) {
    return { ok: true, rules: nonScheduleRules };
  }
  if (rawDate.length === 0) {
    return {
      ok: false,
      error: 'Please choose a date and time for the scheduled link change.',
    };
  }
  const parsedTime = Date.parse(rawDate);
  if (Number.isNaN(parsedTime)) {
    return {
      ok: false,
      error: 'Please enter a valid date and time for the scheduled link change.',
    };
  }
  if (rawTarget.length === 0 || !rawTarget.startsWith('https://')) {
    return {
      ok: false,
      error:
        'Please enter a valid https:// web address for the scheduled link (for example, https://example.com/campaign).',
    };
  }
  const isoTimestamp = new Date(parsedTime).toISOString();
  const scheduledRule: ConditionalRoutingRule = {
    id: 'schedule-1',
    priority: 1,
    active: true,
    condition: {
      validFrom: isoTimestamp,
    },
    targetHref: rawTarget,
    linkType: 'gs1:pip',
  };
  return { ok: true, rules: [scheduledRule, ...nonScheduleRules] };
}
